import { Check, Clock, MapPin } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { ImageWithFallback } from '../figma/ImageWithFallback';
import type { Store } from '@/data';

interface StoreSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  stores: Store[];
  selectedStore: Store;
  onStoreSelect: (store: Store) => void;
  translations: {
    storeSelection: string;
    storeNames: Record<string, string>;
    storeStatus: Record<string, string>;
    cancel: string;
  };
}

export function StoreSelectionModal({
  isOpen,
  onClose,
  stores,
  selectedStore,
  onStoreSelect,
  translations,
}: StoreSelectionModalProps) {
  const getStatusBgColor = (status: string) => {
    switch (status) {
      case '영업중':
        return 'bg-primary';
      case '준비중':
        return 'bg-warning';
      default:
        return 'bg-muted-foreground';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md mx-auto bg-background rounded-[clamp(12px,3vw,20px)] p-0 overflow-hidden">
        <DialogHeader className="p-[clamp(16px,4vw,24px)] pb-0">
          <DialogTitle className="text-[clamp(16px,4vw,20px)] font-bold text-foreground text-center">
            {translations.storeSelection}
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto">
          <div className="p-[clamp(16px,4vw,24px)] space-y-[clamp(8px,2vw,12px)]">
            {stores.map((store) => (
              <button
                key={store.id}
                onClick={() => {
                  onStoreSelect(store);
                  onClose();
                }}
                className={`w-full text-left p-[clamp(12px,3vw,16px)] rounded-[clamp(8px,2vw,12px)] border transition-all duration-200 ${
                  selectedStore.id === store.id
                    ? 'bg-primary/10 border-primary shadow-md'
                    : 'bg-muted border-border hover:bg-muted/80'
                }`}
              >
                <div className="flex items-start gap-[clamp(12px,3vw,16px)]">
                  {/* 매장 이미지 */}
                  <div className="relative w-[clamp(60px,15vw,80px)] h-[clamp(60px,15vw,80px)] shrink-0">
                    <ImageWithFallback
                      src={store.imageUrl}
                      alt={translations.storeNames[store.nameKey as keyof typeof translations.storeNames] || store.name}
                      className="w-full h-full object-cover rounded-[clamp(6px,1.5vw,8px)] shadow-sm"
                    />
                  </div>

                  {/* 매장 정보 */}
                  <div className="flex-1">
                    <div className="flex items-center gap-[clamp(8px,2vw,12px)] mb-[clamp(4px,1vw,8px)]">
                      <h3 className="text-[clamp(14px,3.5vw,16px)] font-bold text-foreground">
                        {translations.storeNames[store.nameKey as keyof typeof translations.storeNames] || store.name}
                      </h3>
                      {selectedStore.id === store.id && (
                        <Check className="h-[clamp(16px,4vw,20px)] w-[clamp(16px,4vw,20px)] text-primary" />
                      )}
                    </div>

                    <div className="flex items-center gap-[clamp(8px,2vw,12px)] mb-[clamp(4px,1vw,8px)]">
                      <div
                        className={`px-[clamp(6px,1.5vw,8px)] py-[clamp(2px,0.5vw,4px)] rounded-[clamp(4px,1vw,6px)] ${getStatusBgColor(store.status)}`}
                      >
                        <span className="text-[clamp(10px,2.5vw,12px)] font-medium text-white">
                          {translations.storeStatus[store.status as keyof typeof translations.storeStatus] || store.status}
                        </span>
                      </div>

                      {store.prep_time && (
                        <div className="flex items-center gap-[clamp(4px,1vw,6px)]">
                          <Clock className="h-[clamp(12px,3vw,14px)] w-[clamp(12px,3vw,14px)] text-primary" />
                          <span className="text-[clamp(10px,2.5vw,12px)] font-medium text-primary">
                            {store.prep_time}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-start gap-[clamp(4px,1vw,6px)]">
                      <MapPin className="h-[clamp(12px,3vw,14px)] w-[clamp(12px,3vw,14px)] text-muted-foreground shrink-0 mt-0.5" />
                      <span className="text-[clamp(10px,2.5vw,12px)] text-muted-foreground line-clamp-2">
                        {store.address}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="p-[clamp(16px,4vw,24px)] pt-0 border-t border-border">
          <Button
            onClick={onClose}
            className="w-full bg-muted text-foreground hover:bg-muted/80 border-none"
          >
            {translations.cancel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
